// 🔧 settings_sponsors.js
// ✅ Purpose: Admin-only Sponsor Levels Settings page
// ✅ Features:
// - Standalone replacement for old Sponsor Levels tab
// - Add, rename, reorder, or remove sponsor levels
// - Prevent deletion/rename if a level is used by any sponsor
// - Loads/saves /events/{eventId}/settings/voting.sponsorLevels
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  hasPurchasedAddOn,
} from "../../utils/event_addons";

const SettingsSponsors = () => {
  const { eventId, eventLoading, ready, eventDoc } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [sponsorAwardsEnabled, setSponsorAwardsEnabled] = useState(true);
  const [sponsorLevels, setSponsorLevels] = useState([]);
  const [usedSponsorLevels, setUsedSponsorLevels] = useState([]);
  const [sponsorsNote, setSponsorsNote] = useState("");
  const [newLevelName, setNewLevelName] = useState("");
  const [saving, setSaving] = useState(false);
  const [sponsorAwardResultsShowAt, setSponsorAwardResultsShowAt] = useState("");

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    loadSettingsAndUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // =====================================================
  // 🎗 Sponsors Add-on Page Gate
  // =====================================================
  // ✅ Purpose:
  // - Prevent direct URL access to sponsor pages when the Sponsors add-on is inactive
  // - Keeps public, sponsor-contact, and manager sponsor tools hidden
  // - Friendly message instead of exposing sponsor functionality
  // =====================================================
  const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor");

  if (!sponsorsEnabled) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-info text-center mb-0">
            Sponsors are not active for this event.
          </div>
        </div>
      </section>
    );
  }

  // =====================================================
  // 📥 Load Sponsor Settings & Usage
  // =====================================================
  // Loads sponsor settings plus determines which sponsor
  // levels are currently in use.
  // =====================================================
  async function loadSettingsAndUsage() {
      if (!eventId) return;

    try {
      setSettingsLoading(true);

      console.log("🎗 Loading sponsor levels for event:", eventId);

      const settingsRef = doc(
        db,
        "events",
        eventId,
        "settings",
        "voting"
      );

      const settingsSnap = await getDoc(settingsRef);
      const data = settingsSnap.exists()
        ? settingsSnap.data()
        : {};

      setSettings(data);
      setSponsorLevels(
        Array.isArray(data.sponsorLevels)
          ? data.sponsorLevels.map((level) => ({
              name: level.name || "",

              // ✅ Backward compatible payment fields
              // ✅ Existing sponsor levels without payment fields safely default to free/manual.
              paymentRequired: level.paymentRequired === true,

              amountCents: Number(level.amountCents || 0),
            }))
          : []
      );
      setSponsorsNote(data.sponsorsNote || "");
      setSponsorAwardsEnabled(data.sponsorAwardsEnabled !== false);
      setSponsorAwardResultsShowAt(data.sponsorAwardResultsShowAt || "");

      const used = new Set();

      const sponsorsSnap = await getDocs(
        collection(db, "events", eventId, "sponsors")
      );

      sponsorsSnap.forEach((docSnap) => {
        const level = docSnap.data()?.level;

        if (level) {
          used.add(level);
        }
      });

      setUsedSponsorLevels([...used]);
    } catch (err) {
      console.error("❌ Failed to load sponsor levels:", err);

      setStatus({
        type: "error",
        message: "❌ Failed to load sponsor levels.",
      });
    } finally {
      setSettingsLoading(false);
    }
  }

  const handleSave = async () => {
    if (!eventId) return;

    setSaving(true);

    try {
      const update = {
        sponsorLevels,
        sponsorsNote,
        sponsorAwardsEnabled,
        lastSaved: Timestamp.now(),
        sponsorAwardResultsShowAt,
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      showStatus(setStatus, {
        type: "success",
        message: "Sponsor levels saved successfully.",
      });
    } catch (err) {
      console.error("❌ Failed to save sponsor levels:", err);

      showStatus(setStatus, {
        type: "error",
        message: "Failed to save sponsor levels.",
      });
    } finally {
      setSaving(false);
    }
  };

  const addLevel = () => {
    const name = newLevelName.trim();

    if (!name) return;

    const exists = sponsorLevels.some(
      (level) => level.name.toLowerCase() === name.toLowerCase()
    );

    if (exists) {
      showStatus(setStatus, {
        type: "warning",
        message: "That sponsor level already exists.",
      });

      return;
    }

    setSponsorLevels([
      ...sponsorLevels,
      {
        name,

        // ✅ Sponsorship payment support
        // ✅ paymentRequired:
        // - true means sponsors assigned to this level must pay before approval
        // - false means sponsors can be approved immediately without payment
        paymentRequired: false,

        // ✅ amountCents:
        // - Stores price in cents to avoid floating point money issues
        // - Example: $500.00 = 50000
        amountCents: 0,
      },
    ]);

    setNewLevelName("");
  };

  const moveLevel = (index, direction) => {
    const newLevels = [...sponsorLevels];
    const swapIndex = direction === "up"
      ? index - 1
      : index + 1;

    if (swapIndex < 0 || swapIndex >= newLevels.length) return;

    [newLevels[index], newLevels[swapIndex]] = [
      newLevels[swapIndex],
      newLevels[index],
    ];

    setSponsorLevels(newLevels);
  };

  const deleteLevel = (index) => {
    setSponsorLevels(
      sponsorLevels.filter((_, levelIndex) => levelIndex !== index)
    );
  };

  const totalSponsorLevels = sponsorLevels.length;
  const lockedSponsorLevels = sponsorLevels.filter((level) =>
    usedSponsorLevels.includes(level.name)
  ).length;

  const lastSavedText = settings?.lastSaved?.toDate?.()
    ? settings.lastSaved.toDate().toLocaleString()
    : "Never";

  if (!ready || eventLoading || settingsLoading || isManager === null) {
    return <FullPageSpinner message="Loading sponsor levels..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">🎗 Sponsor Settings</h1>
            <p className="lead text-muted mb-0">
              Manage sponsor levels, sponsor awards, and public sponsorship content.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Sponsor Settings"}
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert text-start ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-12 col-md-4">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Sponsor Levels</div>
                <div className="fs-3 fw-bold">{totalSponsorLevels}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Levels In Use</div>
                <div className="fs-3 fw-bold">{lockedSponsorLevels}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-4">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Sponsor Awards</div>
                <div className="fs-5 fw-bold">
                  {sponsorAwardsEnabled ? "Active ✅" : "Off"}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Sponsor Level Tools</strong>
          </div>

          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-8">
                <label className="form-label">Add New Sponsor Level</label>

                <input
                  type="text"
                  className="form-control"
                  value={newLevelName}
                  placeholder="Title Sponsor, Gold Sponsor, Community Sponsor..."
                  onChange={(e) => setNewLevelName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      addLevel();
                    }
                  }}
                />

                <div className="form-text">
                  Sponsor levels control grouping and display order on sponsor pages.
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <button
                  type="button"
                  className="btn btn-success w-100"
                  onClick={addLevel}
                  disabled={!newLevelName.trim()}
                >
                  ➕ Add Level
                </button>
              </div>
            </div>
          </div>
        </div>

        {sponsorLevels.length === 0 ? (
          <div className="card shadow-sm mb-4">
            <div className="card-body text-center py-5">
              <h4>No Sponsor Levels Yet</h4>
              <p className="text-muted mb-4">
                Add sponsor levels to organize sponsors on the public event page.
              </p>
            </div>
          </div>
        ) : (
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <strong>Sponsor Levels</strong>
              <div className="small text-muted">
                Rename, reorder, or remove sponsor levels. Levels already used by sponsors are locked.
              </div>
            </div>

            <div className="card-body">
              <div className="row g-4">
                {sponsorLevels.map((level, index) => {
                  const isUsed = usedSponsorLevels.includes(level.name);

                  return (
                    <div key={`${level.name}-${index}`} className="col-12 col-md-6">
                      <div className="card h-100 border shadow-sm">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                            <div>
                              <div className="text-muted small">Display Order</div>
                              <div className="fw-bold">#{index + 1}</div>
                            </div>

                            <span
                              className={`badge ${
                                isUsed ? "bg-warning text-dark" : "bg-primary"
                              }`}
                            >
                              {isUsed ? "In Use" : "Editable"}
                            </span>
                          </div>

                          <label className="form-label small text-muted">
                            Sponsor Level Name
                          </label>

                          <input
                            type="text"
                            className="form-control mb-3"
                            value={level.name}
                            disabled={isUsed}
                            onChange={(e) => {
                              const updated = [...sponsorLevels];

                              updated[index] = {
                                ...updated[index],
                                name: e.target.value,
                              };

                              setSponsorLevels(updated);
                            }}
                            title={
                              isUsed
                                ? "This level is used by a sponsor and cannot be renamed"
                                : ""
                            }
                          />

                          <div className="border rounded p-3 bg-light mb-3">
                            <div className="form-check form-switch mb-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`payment-required-${index}`}
                                checked={level.paymentRequired === true}
                                onChange={(e) => {
                                  const updated = [...sponsorLevels];

                                  updated[index] = {
                                    ...updated[index],

                                    // ✅ Controls whether this sponsor level requires online payment
                                    paymentRequired: e.target.checked,
                                  };

                                  setSponsorLevels(updated);
                                }}
                              />

                              <label
                                className="form-check-label fw-bold"
                                htmlFor={`payment-required-${index}`}
                              >
                                Require Payment For This Level
                              </label>
                            </div>

                            <label className="form-label small text-muted">
                              Sponsorship Fee
                            </label>

                            <div className="input-group">
                              <span className="input-group-text">$</span>

                              <input
                                type="number"
                                min="0"
                                step="1"
                                className="form-control"
                                value={Number(level.amountCents || 0) / 100}
                                onChange={(e) => {
                                  const updated = [...sponsorLevels];

                                  updated[index] = {
                                    ...updated[index],

                                    // ✅ Store money as cents in Firestore
                                    amountCents: Math.round(Number(e.target.value || 0) * 100),
                                  };

                                  setSponsorLevels(updated);
                                }}
                              />
                            </div>

                            <div className="form-text">
                              This fee will be used later when sending sponsors a Stripe payment link.
                            </div>
                          </div>

                          {isUsed && (
                            <div className="small text-danger mb-3">
                              🔒 This level is used by a sponsor and cannot be renamed or deleted.
                            </div>
                          )}

                          <div className="d-flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={index === 0}
                              onClick={() => moveLevel(index, "up")}
                            >
                              ↑ Move Up
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={index === sponsorLevels.length - 1}
                              onClick={() => moveLevel(index, "down")}
                            >
                              ↓ Move Down
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={isUsed}
                              onClick={() => deleteLevel(index)}
                            >
                              🗑 Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🏆 Sponsor Awards</strong>
          </div>

          <div className="card-body">
            <div className="form-check form-switch mb-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="sponsorAwardsEnabled"
                checked={sponsorAwardsEnabled}
                onChange={(e) => setSponsorAwardsEnabled(e.target.checked)}
              />

              <label className="form-check-label fw-bold" htmlFor="sponsorAwardsEnabled">
                Activate Sponsor Awards
              </label>

              <div className="text-muted small">
                When enabled, sponsors can have a Sponsor Award Title shown in admin lists and public sponsor displays.
              </div>
            </div>

            <label className="form-label fw-bold">
              Sponsor Award Results Show Date/Time
            </label>

            <input
              type="datetime-local"
              className="form-control"
              value={sponsorAwardResultsShowAt}
              onChange={(e) => setSponsorAwardResultsShowAt(e.target.value)}
            />

            <div className="form-text">
              Sponsor award winners will be hidden on Event Home until this date and time.
              Leave blank to show results immediately.
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🌟 Become a Sponsor Content</strong>
          </div>

          <div className="card-body">
            <p className="text-muted">
              This content appears on the public Sponsors page and helps potential sponsors learn how to support your event.
            </p>

            <ReactQuill
              theme="snow"
              value={sponsorsNote}
              onChange={setSponsorsNote}
            />
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <strong>Save Sponsor Settings</strong>
              <div className="small text-muted">
                Last saved: {lastSavedText}
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Sponsor Settings"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );

};

export default SettingsSponsors;